import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConnectionsJson, useConnectionsDraftJson, useSaveConnectionsJson, useSaveDraftConnectionsJson } from '@/hooks/useConnections';
import { useAppContextBuilder, useConnections, useDeleteConnection, useSyncConnection } from '@/hooks/useConnectionMutations';
import { usePermission } from '@/hooks/useUserPermissions';
import { useConnectionStore } from '@/stores/connectionStore';
import { useNotificationStore } from '@/stores/notificationStore';
import type { AvailableConnection } from '@/types';
import type { ConnectionReference } from '@/types/connectionReference';
import type {
  AgentConnection,
  ConnectionsConfiguration,
  McpConnection,
} from '@/types/logicApps';
import type { ServiceProviderConnection } from '@/types/connections';
import { deleteConnection, extractConnectionName, getConnection } from '@/services/connectionApi';
import type { ConnectionResource } from '@/services/connectionApi';
import {
  connectionReferenceToLocalRecord,
  getOAuthConnectionId,
  isManagedApiConnector,
  removeOAuthConnectionHolders,
  GITHUB_SERVICE_PROVIDER_CONNECTOR_ID,
} from '@/utils/connections';
import { fetchOAuthConnectionParameterKey } from '@/hooks/useOAuthConnectionParameterKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { telemetryService } from '@/services/telemetryService';

export type ConnectionsTabValue =
  | 'managed'
  | 'serviceProvider'
  | 'agent'
  | 'function'
  | 'mcp'
  | 'raw';

/**
 * Discriminator for connection families that live inside connections.json
 * (or connections-draft.json in draft mode). Used to route the shared
 * delete/save handlers to the right map + telemetry event.
 */
export type LocalConnectionKind = 'serviceProvider' | 'agent' | 'mcp';

/**
 * A GitHub Copilot agent's `connectionId` is deletable as a backing sign-in only when it
 * names a connection in the current app. connections.json is user-editable, so a cross-app
 * or malformed id must never be extracted-and-deleted against this app.
 */
const isConnectionInCurrentApp = (
  connectionId: string,
  projectName: string,
  appName: string,
): boolean => {
  const prefix = `/automationprojects/${projectName.toLowerCase()}/applications/${appName.toLowerCase()}/connections/`;
  const lower = connectionId.toLowerCase();
  if (!lower.startsWith(prefix)) return false;
  // Slice the lowercased id (not the original) so the offset can never drift, then require
  // exactly one connection-name segment — no extra `/` — so a crafted id such as
  // `.../connections/x/connections/victim` cannot be extracted-and-deleted against this app.
  const name = lower.slice(prefix.length);
  return name.length > 0 && !name.includes('/');
};

/** Whether a fetched connection resource resolves to the given `/serviceProviders/{x}` id. */
const isServiceProviderConnection = (
  resource: ConnectionResource,
  serviceProviderId: string,
): boolean => {
  const apiId =
    resource.properties.api?.id ??
    (resource.properties as { connectorId?: string }).connectorId ??
    '';
  return apiId
    .toLowerCase()
    .replace(/\/+$/, '')
    .endsWith(serviceProviderId.toLowerCase().replace(/\/+$/, ''));
};

/** Whether a fetched connection resource is a `/serviceProviders/github` sign-in. */
const isGithubServiceProviderConnection = (resource: ConnectionResource): boolean =>
  isServiceProviderConnection(resource, GITHUB_SERVICE_PROVIDER_CONNECTOR_ID);

/** Value shape accepted by `handleSaveLocalConnection`, keyed by kind. */
export type LocalConnectionValue = {
  serviceProvider: ServiceProviderConnection;
  agent: AgentConnection;
  mcp: McpConnection;
};

export interface UseConnectionsEditorOptions {
  projectName: string;
  appName: string;
  appId: string;
  appPrincipalId?: string;
  /**
   * When false, all data fetches are skipped and state is reset. Use to gate
   * fetches behind dialog-open / app-selected.
   */
  enabled: boolean;
}

export interface ConnectionsEditorState {
  // Identity
  projectName: string;
  appName: string;
  appPrincipalId?: string;

  // Tabs
  selectedTab: ConnectionsTabValue;
  setSelectedTab: (value: ConnectionsTabValue) => void;

  // Permissions
  userCanEdit: boolean;
  permissionsLoading: boolean;

  // Managed connections data
  connections: AvailableConnection[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  getConnectorId: (connection: AvailableConnection) => string;
  handleDeleteConnection: (connection: AvailableConnection) => Promise<void>;
  handleSyncConnection: (connection: AvailableConnection) => Promise<void>;
  isSyncing: boolean;

  // JSON-backed connection mutations (service provider, agent, MCP). Both
  // honor `isDraftMode` — reads from + writes to connections-draft.json when
  // draft mode is on, connections.json otherwise. The `kind` argument picks
  // the right map inside the connections file.
  handleDeleteLocalConnection: (id: string, kind: LocalConnectionKind) => Promise<void>;
  handleSaveLocalConnection: <K extends LocalConnectionKind>(
    id: string,
    kind: K,
    value: LocalConnectionValue[K],
  ) => Promise<void>;
  handleCreateLocalConnection: <K extends LocalConnectionKind>(
    id: string,
    kind: K,
    value: LocalConnectionValue[K],
  ) => Promise<void>;
  handleCreateConnectionReference: (
    id: string,
    reference: ConnectionReference,
  ) => Promise<Exclude<ConnectionsTabValue, 'function' | 'mcp' | 'raw'>>;

  // Agent connections data (from connections.json)
  agentConnections: Record<string, AgentConnection> | undefined;
  // Service provider connections data (from connections.json)
  serviceProviderConnections: Record<string, ServiceProviderConnection> | undefined;
  // MCP connections data (from connections.json)
  mcpConnections: Record<string, McpConnection> | undefined;
  /** True while connections.json is still being fetched (used to avoid a premature empty-state flash) */
  isJsonLoading: boolean;
  /** True if the connections.json fetch errored */
  isJsonError: boolean;
  /** The error from the connections.json fetch, if any */
  jsonFetchError: Error | null;
  /** Call to retry the connections.json fetch */
  refetchConnectionsJson: () => void;

  // JSON editor data
  rawJson: string;
  jsonError: string | null;
  hasChanges: boolean;
  isValidJson: boolean;
  isSaving: boolean;
  handleJsonChange: (value: string) => void;
  handleSave: () => void | Promise<void>;
  handleDiscard: () => void;

  // Draft mode for JSON tab
  isDraftMode: boolean;
  setIsDraftMode: (value: boolean) => void;
  isDraftLoading: boolean;
  isDraftError: boolean;
  refetchDraftJson: () => void;
}

/**
 * Shared state hook for the Connections editor. Backs both the dialog and the
 * full-page variants so the data flow, permission gating, and JSON edit state
 * are identical regardless of presentation chrome.
 */
export function useConnectionsEditor({
  projectName,
  appName,
  appId,
  appPrincipalId,
  enabled,
}: UseConnectionsEditorOptions): ConnectionsEditorState {
  const automationAppId = useMemo(
    () => (projectName && appName ? `/automationProjects/${projectName}/applications/${appName}` : undefined),
    [projectName, appName],
  );

  const { data: userCanEdit = false, isLoading: permissionsLoading } = usePermission(
    enabled ? projectName : '',
    enabled ? appName : undefined,
    ['Contributor'],
  );

  const [selectedTab, setSelectedTab] = useState<ConnectionsTabValue>('managed');
  const [rawJson, setRawJson] = useState('');
  const [originalJson, setOriginalJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isDraftMode, setIsDraftMode] = useState(false);

  const {
    data: connections,
    isLoading,
    isError,
    refetch,
  } = useConnections(enabled ? automationAppId : undefined, undefined, enabled);

  const { data: connectionsJson, isLoading: isJsonLoading, isError: isJsonError, error: jsonFetchError, refetch: refetchConnectionsJson } = useConnectionsJson(
    enabled ? { projectName, appName, appId } : null,
  );

  const { data: draftConnectionsJson, isLoading: isDraftLoading, isError: isDraftError, refetch: refetchDraftJson } = useConnectionsDraftJson(
    enabled ? { projectName, appName, appId } : null,
  );

  const deleteMutation = useDeleteConnection(connectionsJson ?? {}, draftConnectionsJson ?? {});
  const syncMutation = useSyncConnection();
  const { buildContext } = useAppContextBuilder();
  const removeConnectionReference = useConnectionStore((state) => state.removeConnectionReference);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const saveMutation = useSaveConnectionsJson(
    enabled && appId ? { projectName, appName, appId } : null,
  );
  const saveDraftMutation = useSaveDraftConnectionsJson(
    enabled && appId ? { projectName, appName, appId } : null,
  );

  const queryClient = useQueryClient();
  const { getOttoToken } = useAuth();

  // Reset editor + tab whenever the target app identity changes. Without
  // this reset, unsaved edits made against the previous app's
  // connections.json would be preserved by the seed effect (which only
  // re-seeds when there are no pending edits) and could end up being saved
  // against the new app — corrupting the wrong file. Skip the initial mount
  // so we don't clobber the first seed. This effect MUST be declared before
  // the seed effect: when both deps change in the same render (e.g., the
  // dialog is reopened against a different app and connections.json
  // arrives) effects fire in source order, and we need the reset to run
  // first so the seed can repopulate cleanly afterwards.
  const isFirstAppMount = useRef(true);
  useEffect(() => {
    if (isFirstAppMount.current) {
      isFirstAppMount.current = false;
      return;
    }
    setSelectedTab('managed');
    setRawJson('');
    setOriginalJson('');
    setJsonError(null);
  }, [projectName, appName, appId]);

  // Seed (or re-seed) the editor whenever the upstream connections.json changes,
  // as long as the user has no unsaved edits. The previous implementation gated
  // re-seeding behind a one-shot `isInitialized` flag, which meant a `refetch`
  // (or any later cache update) never refreshed the editor — leaving the JSON
  // tab stale or empty if the first fetch landed before the file was ready.
  //
  // When isDraftMode is true, seed from draftConnectionsJson; otherwise use
  // production connectionsJson.
  const activeConnectionsJson = useMemo(() => (isDraftMode ? draftConnectionsJson : connectionsJson), [isDraftMode, draftConnectionsJson, connectionsJson]);
  const activeIsLoading = useMemo(() => (isDraftMode ? isDraftLoading : isJsonLoading), [isDraftMode, isDraftLoading, isJsonLoading]);

  useEffect(() => {
    if (!enabled) {
      // Reset when the editor is closed / no app selected so we re-seed cleanly
      // next time it opens.
      setRawJson('');
      setOriginalJson('');
      setJsonError(null);
      return;
    }
    if (activeIsLoading || !activeConnectionsJson) return;

    const json = JSON.stringify(activeConnectionsJson, null, 2);
    setOriginalJson((prevOriginal) => {
      // Only re-seed the editor's working copy when the user hasn't started
      // editing — otherwise their unsaved changes would be wiped out by a
      // background refetch.
      setRawJson((prevRaw) => (prevRaw === prevOriginal ? json : prevRaw));
      return json;
    });
  }, [enabled, activeConnectionsJson, activeIsLoading]);

  // Re-seed immediately when draft mode is toggled (force reseed by resetting
  // original so the next effect run will see prevRaw === prevOriginal).
  const prevDraftMode = useRef(isDraftMode);
  useEffect(() => {
    if (prevDraftMode.current !== isDraftMode) {
      prevDraftMode.current = isDraftMode;
      const source = isDraftMode ? draftConnectionsJson : connectionsJson;
      if (source) {
        const json = JSON.stringify(source, null, 2);
        setRawJson(json);
        setOriginalJson(json);
        setJsonError(null);
      }
    }
  }, [isDraftMode, draftConnectionsJson, connectionsJson]);

  const hasChanges = rawJson !== originalJson;
  const isValidJson = jsonError === null;

  const handleJsonChange = useCallback((value: string) => {
    setRawJson(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  }, []);

  const handleSave = useCallback(async () => {
    let parsed: ConnectionsConfiguration;
    try {
      parsed = JSON.parse(rawJson) as ConnectionsConfiguration;
    } catch (e) {
      setJsonError((e as Error).message);
      return;
    }

    try {
      if (isDraftMode) {
        await saveDraftMutation.mutateAsync(parsed);
      } else {
        await saveMutation.mutateAsync(parsed);
      }
      setOriginalJson(rawJson);
      addNotification({
        type: 'success',
        title: 'Connections saved',
        message: 'connections.json has been updated.',
      });
      telemetryService.trackEvent('connections.saved');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save connections';
      addNotification({
        type: 'error',
        title: 'Save failed',
        message,
      });
      telemetryService.trackException(error as Error, { context: 'connections.save' });
    }
  }, [rawJson, isDraftMode, addNotification, saveDraftMutation, saveMutation]);

  const handleDiscard = useCallback(() => {
    setRawJson(originalJson);
    setJsonError(null);
  }, [originalJson]);

  const handleDeleteConnection = useCallback(
    async (connection: AvailableConnection) => {
      try {
        await deleteMutation.mutateAsync({ connectionName: connection.name, connectorId: connection.connectorId ?? '' });
        // Keep the node-editor connection store in sync so freshly opened
        // panels don't reference the now-deleted connection.
        removeConnectionReference(connection.name);
        void refetch();
        addNotification({
          type: 'success',
          title: 'Connection deleted',
          message: `${connection.displayName ?? connection.name} was removed.`,
        });
        telemetryService.trackEvent('connection.deleted');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete connection';
        addNotification({
          type: 'error',
          title: 'Delete failed',
          message,
        });
        telemetryService.trackException(error as Error, { context: 'connection.delete' });
        // Re-throw so the caller (e.g. the confirmation dialog) can keep its
        // pending state and let the user retry without closing.
        throw error;
      }
    },
    [deleteMutation, removeConnectionReference, refetch, addNotification],
  );

  const handleSyncConnection = useCallback(
    async (connection: AvailableConnection) => {
      try {
        await syncMutation.mutateAsync(connection.name);
        void refetch();
        addNotification({
          type: 'success',
          title: 'Connection synced',
          message: `${connection.displayName ?? connection.name} was synchronized.`,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to sync connection';
        addNotification({
          type: 'error',
          title: 'Sync failed',
          message,
        });
        telemetryService.trackException(error as Error, { context: 'connection.sync' });
        throw error;
      }
    },
    [syncMutation, refetch, addNotification],
  );

  const getConnectorId = useCallback(
    (connection: AvailableConnection) => {
      const connector = connection.connectorId ?? '';
      // Canonical /managedApis/{name} format — expand to the full data-plane
      // path so downstream connector-metadata lookups can resolve it.
      const match = connector.match(/^\/managedApis\/([^/]+)$/i);
      if (match?.[1]) {
        return `/automationProjects/${projectName}/managedApis/${match[1]}`;
      }
      return connector;
    },
    [projectName],
  );

  // ── JSON-backed connection mutations ────────────────────────────────────
  // Service-provider / agent / MCP connections live inside connections.json
  // (or connections-draft.json in draft mode) rather than as ARM resources,
  // so edit/delete is a JSON patch on the file followed by a save. The
  // active source + save mutation are picked off `isDraftMode` so the user's
  // current view (Draft vs Production) is the one mutated.
  const mutateActiveConnections = useCallback(
    async (
      mutator: (current: ConnectionsConfiguration) => ConnectionsConfiguration,
      successMessage: { title: string; message: string; event: string },
      failureContext: string,
    ): Promise<void> => {
      // Snapshot the current source so concurrent draft-mode toggles can't
      // swap the target file under our feet between read and write.
      const draftMode = isDraftMode;
      const source = draftMode ? draftConnectionsJson : connectionsJson;
      if (!source) {
        // Nothing loaded yet — refuse rather than seeding an empty file that
        // would clobber out-of-band edits when the real fetch lands.
        throw new Error('Connections file is not loaded yet.');
      }
      const next = mutator(source);

      // Skip the round-trip when the mutator produced a byte-identical
      // file. Callers still pass through a fresh object graph on every
      // edit (record spreads always allocate), so reference equality is
      // never enough — compare canonical JSON. Common triggers:
      //   - Save on an SP dialog where the display-name was edited then
      //     reverted to the original value.
      //   - Delete of an id that isn't present in the map (delete no-op).
      //   - Save with a value deep-equal to what's already stored.
      // The user still gets the success notification so the UX matches a
      // real save, but we avoid an unnecessary POST + refetch.
      if (JSON.stringify(next) === JSON.stringify(source)) {
        addNotification({
          type: 'success',
          title: successMessage.title,
          message: successMessage.message,
        });
        telemetryService.trackEvent(`${successMessage.event}.noop`);
        return;
      }

      try {
        if (draftMode) {
          await saveDraftMutation.mutateAsync(next);
        } else {
          await saveMutation.mutateAsync(next);
        }
        addNotification({
          type: 'success',
          title: successMessage.title,
          message: successMessage.message,
        });
        telemetryService.trackEvent(successMessage.event);
        if (draftMode) {
          await refetchDraftJson();
        } else {
          await refetchConnectionsJson();
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to update connection';
        addNotification({ type: 'error', title: 'Update failed', message });
        telemetryService.trackException(error as Error, { context: failureContext });
        throw error;
      }
    },
    [
      isDraftMode,
      draftConnectionsJson,
      connectionsJson,
      saveDraftMutation,
      saveMutation,
      addNotification,
      refetchDraftJson,
      refetchConnectionsJson,
    ],
  );

  // Per-kind config for the two shared handlers below. Keeps the family →
  // map-key / label / telemetry-slug mapping in one place so adding a new
  // JSON-backed connection family is a one-line edit here.
  const LOCAL_KIND_CONFIG: Record<
    LocalConnectionKind,
    {
      mapKey: 'serviceProviderConnections' | 'agentConnections' | 'agentMcpConnections';
      label: string;
      telemetrySlug: string;
    }
  > = useMemo(
    () => ({
      serviceProvider: {
        mapKey: 'serviceProviderConnections',
        label: 'Service provider connection',
        telemetrySlug: 'serviceProvider',
      },
      agent: {
        mapKey: 'agentConnections',
        label: 'Agent connection',
        telemetrySlug: 'agent',
      },
      mcp: {
        mapKey: 'agentMcpConnections',
        label: 'MCP connection',
        telemetrySlug: 'mcp',
      },
    }),
    [],
  );

  const handleDeleteLocalConnection = useCallback(
    async (id: string, kind: LocalConnectionKind): Promise<void> => {
      const { mapKey, label, telemetrySlug } = LOCAL_KIND_CONFIG[kind];
      const activeConfig = isDraftMode ? draftConnectionsJson : connectionsJson;
      const holder =
        kind === 'serviceProvider'
          ? activeConfig?.serviceProviderConnections?.[id]
          : undefined;

      // Resolved here rather than from a cached map so a half-loaded connector can't
      // make an OAuth-backed connection look file-only and skip the cascade.
      let oauthParameterKey: string | undefined;
      if (holder?.serviceProvider?.id) {
        try {
          oauthParameterKey = await fetchOAuthConnectionParameterKey(queryClient, {
            connectorId: holder.serviceProvider.id,
            parameterSetName: holder.parameterSetName,
            projectName,
            appName,
            getToken: getOttoToken,
          });
        } catch (error) {
          // Callers keep the confirmation dialog open on a throw and rely on this hook
          // having reported the reason.
          const message =
            error instanceof Error ? error.message : 'Failed to delete connection';
          addNotification({ type: 'error', title: 'Delete failed', message });
          telemetryService.trackException(error as Error, {
            context: `connection.${telemetrySlug}.delete`,
          });
          throw error;
        }
      }

      const backingOAuthConnectionId = oauthParameterKey
        ? getOAuthConnectionId(holder, oauthParameterKey)
        : undefined;

      // GitHub Copilot agent connections carry the resource id of their backing
      // /serviceProviders/github sign-in in `connectionId`. That credential is hidden from
      // the Connections list, so removing only the agent row would strand it — cascade the
      // delete too. connections.json is user-editable, so the cascade is gated: only a
      // GitHub Copilot agent whose id resolves to a github sign-in in THIS app is eligible.
      const agentConnection =
        kind === 'agent' ? activeConfig?.agentConnections?.[id] : undefined;
      const agentBackingConnectionId =
        agentConnection?.type === 'GitHubCopilot' ? agentConnection.connectionId : undefined;

      if (agentBackingConnectionId) {
        // Backing ids are compared case-insensitively (as elsewhere): draft and production may
        // serialize the same id with different casing, and a case-sensitive miss here would
        // delete a sign-in the other file still references.
        const backingIdLower = agentBackingConnectionId.toLowerCase();
        const anyRefOutsideDeleted = (
          cfg: ConnectionsConfiguration | null | undefined,
          skipKey?: string,
        ): boolean => {
          const agentRef = Object.entries(cfg?.agentConnections ?? {}).some(
            ([key, conn]) => key !== skipKey && conn.connectionId?.toLowerCase() === backingIdLower,
          );
          if (agentRef) return true;
          // A service-provider holder (e.g. a GitHub operation connection) can point at the
          // same app-scoped sign-in via its parameter values; deleting the sign-in would break
          // that connection too. Match the full resource id anywhere in its values.
          return Object.values(cfg?.serviceProviderConnections ?? {}).some((sp) =>
            Object.values(sp?.parameterValues ?? {}).some(
              (value) => typeof value === 'string' && value.toLowerCase() === backingIdLower,
            ),
          );
        };

        try {
          // Both files must be loaded: an unloaded file reads as "no references" and could
          // delete a shared sign-in the other file still points at (mirrors the SP cascade).
          if (!connectionsJson || !draftConnectionsJson) {
            throw new Error('Connections file is not loaded yet.');
          }

          // The active file loses `id`; the other file is unchanged (a shared sign-in there
          // must keep the credential alive).
          const referencedElsewhere = isDraftMode
            ? anyRefOutsideDeleted(draftConnectionsJson, id) ||
              anyRefOutsideDeleted(connectionsJson)
            : anyRefOutsideDeleted(connectionsJson, id) ||
              anyRefOutsideDeleted(draftConnectionsJson);

          // Files first: a later credential-delete failure then leaves a retryable orphan
          // rather than a row pointing at a deleted sign-in.
          const source = isDraftMode ? draftConnectionsJson : connectionsJson;
          const nextAgents = { ...(source.agentConnections ?? {}) };
          delete nextAgents[id];
          const nextConfig = { ...source, agentConnections: nextAgents };
          if (isDraftMode) {
            await saveDraftMutation.mutateAsync(nextConfig);
          } else {
            await saveMutation.mutateAsync(nextConfig);
          }

          let signInError: string | undefined;
          if (!referencedElsewhere) {
            try {
              // Never delete an arbitrary/cross-app id: the backing connection must live in
              // this app and actually resolve to a /serviceProviders/github sign-in.
              if (!isConnectionInCurrentApp(agentBackingConnectionId, projectName, appName)) {
                throw new Error('backing connection does not belong to this app');
              }
              const ctx = await buildContext();
              if (!ctx) throw new Error('No app context available.');
              const connectionName = extractConnectionName(agentBackingConnectionId);
              const backing = await getConnection(ctx, connectionName);
              if (!isGithubServiceProviderConnection(backing)) {
                throw new Error('backing connection is not a GitHub sign-in');
              }
              await deleteConnection(ctx, connectionName);
            } catch (cleanupError) {
              signInError =
                cleanupError instanceof Error ? cleanupError.message : String(cleanupError);
              telemetryService.trackException(cleanupError as Error, {
                context: `connection.${telemetrySlug}.deleteOAuthConnection`,
              });
            }
          }

          addNotification(
            signInError
              ? {
                  type: 'warning',
                  title: 'Connection deleted, sign-in kept',
                  message: `${label} "${id}" was removed, but its GitHub sign-in could not be deleted: ${signInError}`,
                }
              : {
                  type: 'success',
                  title: 'Connection deleted',
                  message: referencedElsewhere
                    ? `${label} "${id}" was removed.`
                    : `${label} "${id}" and its GitHub sign-in were removed.`,
                },
          );
          telemetryService.trackEvent(`connection.${telemetrySlug}.deleted`);

          await refetchConnectionsJson();
          await refetchDraftJson();
          void refetch();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete connection';
          addNotification({ type: 'error', title: 'Delete failed', message });
          telemetryService.trackException(error as Error, {
            context: `connection.${telemetrySlug}.delete`,
          });
          await Promise.allSettled([refetchConnectionsJson(), refetchDraftJson()]);
          void refetch();
          throw error;
        }
        return;
      }

      if (!oauthParameterKey || !backingOAuthConnectionId) {
        await mutateActiveConnections(
          (current) => {
            const next = { ...(current[mapKey] ?? {}) };
            delete next[id];
            return { ...current, [mapKey]: next };
          },
          {
            title: 'Connection deleted',
            message: `${label} "${id}" was removed.`,
            event: `connection.${telemetrySlug}.deleted`,
          },
          `connection.${telemetrySlug}.delete`,
        );
        return;
      }

      // The credential is an app-scoped resource shared by every holder that points at it,
      // so it cannot be deleted for one and kept for another: the siblings go together.
      try {
        // An unloaded file reads as "holder absent", which would delete the sign-in and
        // leave the holder behind pointing at it.
        if (!connectionsJson || !draftConnectionsJson) {
          throw new Error('Connections file is not loaded yet.');
        }

        const nextConnections = removeOAuthConnectionHolders(
          connectionsJson,
          backingOAuthConnectionId,
          oauthParameterKey,
        );
        const nextDraft = removeOAuthConnectionHolders(
          draftConnectionsJson,
          backingOAuthConnectionId,
          oauthParameterKey,
        );
        const removedKeys = new Set([
          ...(nextConnections?.removedKeys ?? []),
          ...(nextDraft?.removedKeys ?? []),
        ]);

        // Files first: a failure here leaves the holders and their sign-in both intact and
        // retryable. Deleting first would strand a holder pointing at a sign-in that no
        // longer exists, and the retry would fail on the now-404 delete.
        if (nextConnections) {
          await saveMutation.mutateAsync(nextConnections.config);
        }
        if (nextDraft) {
          await saveDraftMutation.mutateAsync(nextDraft.config);
        }

        // A GitHub Copilot agent can share this sign-in; if any agent still points at it, keep
        // the resource even though the service-provider holders are gone (mirror of the agent
        // cascade's check in the other direction).
        const spBackingIdLower = backingOAuthConnectionId.toLowerCase();
        const agentStillReferences = [connectionsJson, draftConnectionsJson].some((cfg) =>
          Object.values(cfg?.agentConnections ?? {}).some(
            (conn) => conn.connectionId?.toLowerCase() === spBackingIdLower,
          ),
        );

        let signInError: string | undefined;
        if (!agentStillReferences) {
          try {
            // backingOAuthConnectionId comes from user-editable connections.json, so validate
            // it resolves to exactly one connection in THIS app and actually matches the
            // holder's own service provider before deleting — otherwise a crafted cross-app/
            // nested id could delete an unrelated same-named connection (mirror of the agent
            // cascade's checks above). This branch handles any OAuth service provider (github,
            // ado-build, ...), so validate against the holder, not a hard-coded GitHub kind.
            const holderServiceProviderId = holder?.serviceProvider?.id;
            if (!holderServiceProviderId) {
              throw new Error('holder is missing a service provider id');
            }
            if (!isConnectionInCurrentApp(backingOAuthConnectionId, projectName, appName)) {
              throw new Error('backing connection does not belong to this app');
            }
            const ctx = await buildContext();
            if (!ctx) throw new Error('No app context available.');
            const connectionName = extractConnectionName(backingOAuthConnectionId);
            const backing = await getConnection(ctx, connectionName);
            if (!isServiceProviderConnection(backing, holderServiceProviderId)) {
              throw new Error("backing connection does not match the holder's service provider");
            }
            await deleteConnection(ctx, connectionName);
          } catch (cleanupError) {
            // The holders are already gone, so failing the delete the user asked for would
            // only trap them. The sign-in is reported instead of silently orphaned.
            signInError =
              cleanupError instanceof Error ? cleanupError.message : String(cleanupError);
            telemetryService.trackException(cleanupError as Error, {
              context: `connection.${telemetrySlug}.deleteOAuthConnection`,
            });
          }
        }

        const alsoRemoved = removedKeys.size - 1;
        const siblings = alsoRemoved > 0 ? ` and ${alsoRemoved} sharing its sign-in` : '';

        let notification;
        if (signInError) {
          notification = {
            type: 'warning' as const,
            title: 'Connection deleted, sign-in kept',
            message: `${label} "${id}"${siblings} was removed, but its sign-in could not be deleted: ${signInError}`,
          };
        } else if (agentStillReferences) {
          // The sign-in was intentionally kept because a Copilot agent still uses it, so don't
          // claim it was removed.
          notification = {
            type: 'success' as const,
            title: 'Connection deleted, sign-in kept',
            message: `${label} "${id}"${siblings} was removed. Its sign-in was kept because an agent still uses it.`,
          };
        } else {
          notification = {
            type: 'success' as const,
            title: 'Connection deleted',
            message: `${label} "${id}"${siblings} and its sign-in were removed.`,
          };
        }
        addNotification(notification);
        telemetryService.trackEvent(`connection.${telemetrySlug}.deleted`);

        await refetchConnectionsJson();
        await refetchDraftJson();
        void refetch();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete connection';
        addNotification({ type: 'error', title: 'Delete failed', message });
        telemetryService.trackException(error as Error, {
          context: `connection.${telemetrySlug}.delete`,
        });
        // A throw between the two file saves still leaves the first one written, so the
        // retry has to start from what is on disk rather than from the pre-delete state.
        // Settled rather than awaited in sequence: a failing refetch must not mask the
        // delete error or skip the refetches after it.
        await Promise.allSettled([refetchConnectionsJson(), refetchDraftJson()]);
        void refetch();
        throw error;
      }
    },
    [
      mutateActiveConnections,
      LOCAL_KIND_CONFIG,
      isDraftMode,
      draftConnectionsJson,
      connectionsJson,
      buildContext,
      saveMutation,
      saveDraftMutation,
      addNotification,
      refetchConnectionsJson,
      refetchDraftJson,
      refetch,
      queryClient,
      projectName,
      appName,
      getOttoToken,
    ],
  );

  const handleSaveLocalConnection = useCallback(
    async <K extends LocalConnectionKind>(
      id: string,
      kind: K,
      value: LocalConnectionValue[K],
    ): Promise<void> => {
      const { mapKey, label, telemetrySlug } = LOCAL_KIND_CONFIG[kind];
      await mutateActiveConnections(
        (current) => ({
          ...current,
          [mapKey]: {
            ...(current[mapKey] ?? {}),
            [id]: value,
          },
        }),
        {
          title: 'Connection saved',
          message: `${label} "${id}" was updated.`,
          event: `connection.${telemetrySlug}.saved`,
        },
        `connection.${telemetrySlug}.save`,
      );

      // Signing in creates or renews the backing OAuth connection, so the status column
      // reads stale until the data-plane list is refetched.
      if (kind === 'serviceProvider') {
        void refetch();
      }
    },
    [mutateActiveConnections, LOCAL_KIND_CONFIG, refetch],
  );

  const handleCreateLocalConnection = useCallback(
    async <K extends LocalConnectionKind>(
      id: string,
      kind: K,
      value: LocalConnectionValue[K],
    ): Promise<void> => {
      const { mapKey, label, telemetrySlug } = LOCAL_KIND_CONFIG[kind];
      try {
        const draftMode = isDraftMode;
        const refreshed = draftMode
          ? await refetchDraftJson()
          : await refetchConnectionsJson();
        if (refreshed.isError || !refreshed.data) {
          throw new Error('Could not load the latest connection list. Refresh and try again.');
        }

        const current = refreshed.data;
        const currentRecords = current[mapKey] ?? {};
        if (Object.hasOwn(currentRecords, id)) {
          throw new Error(`${label} "${id}" already exists. Choose a different name.`);
        }

        const next = {
          ...current,
          [mapKey]: {
            ...currentRecords,
            [id]: value,
          },
        };
        if (draftMode) {
          await saveDraftMutation.mutateAsync(next);
          await refetchDraftJson();
        } else {
          await saveMutation.mutateAsync(next);
          await refetchConnectionsJson();
        }

        addNotification({
          type: 'success',
          title: 'Connection added',
          message: `${label} "${id}" was added.`,
        });
        telemetryService.trackEvent(`connection.${telemetrySlug}.created`);

        if (kind === 'serviceProvider') {
          await refetch();
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to add connection';
        addNotification({ type: 'error', title: 'Add failed', message });
        telemetryService.trackException(error as Error, {
          context: `connection.${telemetrySlug}.create`,
        });
        throw error;
      }
    },
    [
      LOCAL_KIND_CONFIG,
      addNotification,
      isDraftMode,
      refetch,
      refetchConnectionsJson,
      refetchDraftJson,
      saveDraftMutation,
      saveMutation,
    ],
  );

  const handleCreateConnectionReference = useCallback(
    async (
      id: string,
      reference: ConnectionReference,
    ): Promise<Exclude<ConnectionsTabValue, 'function' | 'mcp' | 'raw'>> => {
      const localRecord = connectionReferenceToLocalRecord(reference, id);
      if (localRecord?.kind === 'serviceProvider') {
        await handleCreateLocalConnection(id, 'serviceProvider', localRecord.value);
        return 'serviceProvider';
      }
      if (localRecord?.kind === 'agent') {
        await handleCreateLocalConnection(id, 'agent', localRecord.value);
        return 'agent';
      }
      if (!isManagedApiConnector(reference.api.id)) {
        throw new Error('This connection type cannot be added at the app level.');
      }

      await refetch();
      addNotification({
        type: 'success',
        title: 'Connection added',
        message: `${reference.connectionName ?? id} was added.`,
      });
      return 'managed';
    },
    [addNotification, handleCreateLocalConnection, refetch],
  );

  return {
    projectName,
    appName,
    ...(appPrincipalId ? { appPrincipalId } : {}),
    selectedTab,
    setSelectedTab,
    userCanEdit,
    permissionsLoading,
    connections,
    isLoading,
    isError,
    refetch,
    getConnectorId,
    handleDeleteConnection,
    handleSyncConnection,
    isSyncing: syncMutation.isPending,
    handleDeleteLocalConnection,
    handleSaveLocalConnection,
    handleCreateLocalConnection,
    handleCreateConnectionReference,
    // Tabbed lists follow draft mode just like the raw JSON editor does, so
    // template-applied placeholders (which only land in connections-draft.json)
    // appear in the same view the user is editing. The Connections-view filters
    // still hide unusable placeholder rows.
    agentConnections: activeConnectionsJson?.agentConnections,
    serviceProviderConnections: activeConnectionsJson?.serviceProviderConnections,
    mcpConnections: activeConnectionsJson?.agentMcpConnections,
    isJsonLoading,
    isJsonError,
    jsonFetchError: jsonFetchError ?? null,
    refetchConnectionsJson,
    rawJson,
    jsonError,
    hasChanges,
    isValidJson,
    isSaving: saveMutation.isPending,
    handleJsonChange,
    handleSave,
    handleDiscard,
    isDraftMode,
    setIsDraftMode,
    isDraftLoading,
    isDraftError,
    refetchDraftJson,
  };
}
